import React, { useEffect } from 'react';
import {
  arrayOf,
  bool,
  extend,
  fragment,
  params,
  shape,
  string,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { Metadata } from '@thd-olt-component-react/metadata';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { RichText } from '@thd-olt-component-react/rich-text';
import { Typography } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind.js';
import {
  getAnalyticsPageType,
  getContentBreadCrumbLabels,
  renderRobotTag
} from '../../utils/utils';
import styles from '../../templates/giftCard-layout.scss';

export const SiteMapPage = ({ slug, customerType, pathWithQueryParams, previewLink }) => {

  const { data, loading, error } = useDataModel('layouts', {
    variables: {
      slug,
      customerType,
      isHybrid: false
    },
  });
  const cx = classNames.bind(styles);
  const layouts = data?.layouts;
  const crumbs = layouts?.breadcrumbs?.breadcrumbItem || [];
  const metadata = layouts?.seo || {};
  const { linkData, scriptData, ...metadataMinusLinkDataAndScriptData } = metadata;

  const breadCrumbLabels = getContentBreadCrumbLabels(crumbs);
  const pageType = getAnalyticsPageType(pathWithQueryParams);
  const breadcrumbs = {
    searchModel: {
      taxonomy: {
        breadCrumbs: crumbs
      }
    }
  };
  const mainSection = layouts?.content?.dynamicSections[0];
  const richTextsList = mainSection?.components.length > 0
    ? mainSection?.components.map((item, index) => { return item?.richTextContent; }) : null;

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('speciality-content.page-load', {
      breadCrumbLabels,
      pageType
    });
  }, [data]);

  return (
    <Row>
      <ErrorBoundary name="metadata">
        <Metadata data={{ ...metadataMinusLinkDataAndScriptData }}>
          {renderRobotTag(metadata, previewLink)}
        </Metadata>
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('layout-top__no-bottom-padding')}>
          <div>
            {breadcrumbs && (
              <ErrorBoundary name="breadcrumbs">
                <QueryProvider cacheKey="sitemap-breadcrumbs">
                  <Breadcrumbs staticCrumbs={crumbs} data={breadcrumbs} />
                </QueryProvider>
              </ErrorBoundary>
            )}
          </div>
        </Col>
        <Col xs="12">
          <div>
            <ErrorBoundary name="title">
              <Typography variant="h1" weight="display" height="snug" color="primary" uppercase>
                {data?.layouts?.title}
              </Typography>
            </ErrorBoundary>
          </div>
          <div className="sui-grid md:sui-grid-cols-4 sui-gap-2 sui-p-3 sm:sui-grid-cols-1 lg:sui-grid-cols-5">
            {
              richTextsList && richTextsList?.map((item, index) => (
                <div>
                  <ErrorBoundary name="rich-text">
                    <RichText content={item} />
                  </ErrorBoundary>
                </div>
              ))
            }
          </div>
        </Col>
      </Row>
    </Row>
  );
};

SiteMapPage.displayName = 'SiteMapPage';
SiteMapPage.propTypes = {
  slug: PropTypes.string.isRequired,
  customerType: PropTypes.string.isRequired,
  pathWithQueryParams: PropTypes.string.isRequired,
  previewLink: PropTypes.string.isRequired,
};
SiteMapPage.dataModel = extend(
  {
    layouts: params({
      slug: string().isRequired(),
      customerType: string(),
      isHybrid: bool(),
    }).shape({
      title: string(),
      breadcrumbs: shape({
        __typename: string(),
        Breadcrumbs: fragment().shape({
          __typename: string(),
          breadcrumbItem: shape({
            BreadcrumbItem: fragment().shape({
              label: string(), url: string()
            })
          })
        })
      }),
      seo: shape({
        canonicalURL: string(),
        description: string(),
        facebookAdmins: string(),
        siteName: string(),
        title: string(),
        type: string(),
        linkData: string(),
        scriptData: string(),
        no_index: string(),
        no_follow: string()
      }),
      content: shape({
        __typename: string(),
        HybridPLPLayout: fragment().shape({
          dynamicSections: arrayOf(shape({
            components: arrayOf(shape({
              Spotlight: fragment().shape({
                title: string(),
                richTextContent: string()
              })
            }))
          }))
        })
      }),
    }),
  });
